import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { teacherModuleAPI, timesheetsAPI, getApiBaseURL } from '../../services/api';
import { roleLabel, weekStatusLabel } from '../Payroll/plainLabels';
import TimesheetWeekGrid from './TimesheetWeekGrid';

const ALL_TEACHERS = { id: '', label: 'All teachers' };

function matchesQuery(parts, query) {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return true;
  return parts
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
    .includes(q);
}

export default function TimesheetInboxPage() {
  const qc = useQueryClient();
  const { isAdmin, isTeacherAdmin, isTeacherSupervisor, isFinanceSupervisor } = useAuth();
  const [status, setStatus] = useState('submitted');
  const [month, setMonth] = useState('');
  const [teacherId, setTeacherId] = useState('');
  const [selected, setSelected] = useState(null);
  const [comment, setComment] = useState('');
  const canPickTeacher = isAdmin() || isTeacherAdmin() || isTeacherSupervisor() || isFinanceSupervisor();

  const teachersQuery = useQuery(
    ['timesheet-inbox-teachers'],
    async () => {
      const res = await teacherModuleAPI.getViewableTeachers();
      return res.data?.data?.teachers || [];
    },
    { enabled: canPickTeacher, staleTime: 5 * 60_000 }
  );

  const teacherOptions = useMemo(() => {
    const list = (teachersQuery.data || [])
      .map((t) => ({
        id: String(t.id),
        label: t.name || `${t.first_name || ''} ${t.last_name || ''}`.trim() || t.email || 'Teacher',
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
    return [ALL_TEACHERS, ...list];
  }, [teachersQuery.data]);

  const selectedTeacher =
    teacherOptions.find((t) => t.id === teacherId) ||
    (teacherId ? { id: teacherId, label: 'Selected teacher' } : ALL_TEACHERS);

  const inboxQuery = useQuery(
    ['timesheet-inbox', status, month, teacherId],
    async () => {
      const params = {};
      if (status) params.status = status;
      if (month) params.month = month;
      if (teacherId) params.teacherId = teacherId;
      const res = await timesheetsAPI.getInbox(params);
      return res.data.data || [];
    }
  );

  const detailQuery = useQuery(
    ['timesheet-detail', selected],
    async () => {
      const res = await timesheetsAPI.getWeek(selected);
      return res.data.data;
    },
    { enabled: !!selected }
  );

  const reviewMut = useMutation(
    ({ action }) => timesheetsAPI.reviewWeek(selected, { action, comment }),
    {
      onSuccess: () => {
        toast.success('Updated');
        setComment('');
        qc.invalidateQueries(['timesheet-inbox']);
        qc.invalidateQueries(['timesheet-detail']);
      },
      onError: (e) => toast.error(e.response?.data?.message || 'Failed'),
    }
  );

  const downloadCsv = async (grain) => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({ grain });
      if (month) params.set('month', month);
      if (teacherId) params.set('teacherId', teacherId);
      const url = `${getApiBaseURL()}/api/timesheets/export.csv?${params.toString()}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `timesheets-${grain}.csv`;
      a.click();
    } catch (e) {
      toast.error(e.message || 'Export failed');
    }
  };

  const closeReview = () => {
    setSelected(null);
    setComment('');
  };

  return (
    <Box sx={{ p: 2, maxWidth: 1200, mx: 'auto' }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" mb={2} spacing={2}>
        <Box>
          <Typography variant="h5">Timesheet inbox</Typography>
          <Typography variant="body2" color="text.secondary">
            Approve the weeks that are waiting. People fill them on Timesheet.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" onClick={() => downloadCsv('week')}>
            Download by week
          </Button>
          <Button variant="outlined" onClick={() => downloadCsv('month')}>
            Download by month
          </Button>
        </Stack>
      </Stack>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={2}>
        <TextField select size="small" label="Status" value={status} onChange={(e) => setStatus(e.target.value)} sx={{ minWidth: 160 }}>
          <MenuItem value="submitted">Waiting</MenuItem>
          <MenuItem value="approved">Approved</MenuItem>
          <MenuItem value="rejected">Sent back</MenuItem>
          <MenuItem value="">Everything</MenuItem>
        </TextField>
        <TextField
          size="small"
          type="month"
          label="Month"
          InputLabelProps={{ shrink: true }}
          value={month}
          onChange={(e) => setMonth(e.target.value)}
        />
        {canPickTeacher ? (
          <Autocomplete
            size="small"
            options={teacherOptions}
            value={selectedTeacher}
            onChange={(_e, next) => setTeacherId(next?.id || '')}
            getOptionLabel={(option) => option?.label || ''}
            isOptionEqualToValue={(a, b) => String(a?.id || '') === String(b?.id || '')}
            filterOptions={(opts, state) =>
              opts.filter((option) => matchesQuery([option?.label], state.inputValue))
            }
            autoHighlight
            autoComplete
            includeInputInList
            clearOnEscape
            renderInput={(params) => (
              <TextField {...params} label="Teacher" placeholder="Type to search" />
            )}
            sx={{ minWidth: 240 }}
          />
        ) : null}
      </Stack>

      {inboxQuery.isLoading && <CircularProgress />}
      {inboxQuery.isError && <Alert severity="error">Could not load the weeks.</Alert>}
      {!inboxQuery.isLoading && (inboxQuery.data || []).length === 0 && (
        <Alert severity="info">Nothing is waiting for this filter.</Alert>
      )}

      <Paper>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Person</TableCell>
              <TableCell>Job</TableCell>
              <TableCell>Week</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Expected hours</TableCell>
              <TableCell align="right">Worked</TableCell>
              <TableCell align="right">Leave</TableCell>
              <TableCell align="right">Difference</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {(inboxQuery.data || []).map((row) => (
              <TableRow key={row.id} hover>
                <TableCell>{row.ownerName}</TableCell>
                <TableCell>{roleLabel(row.ownerRole)}</TableCell>
                <TableCell>
                  {row.weekStart} → {row.weekEnd}
                </TableCell>
                <TableCell>
                  <Chip size="small" label={weekStatusLabel(row.status)} />
                </TableCell>
                <TableCell align="right">{row.expectedHours}</TableCell>
                <TableCell align="right">{row.workedHours}</TableCell>
                <TableCell align="right">{row.leaveHours}</TableCell>
                <TableCell align="right">{row.varianceHours}</TableCell>
                <TableCell>
                  <Button size="small" onClick={() => setSelected(row.id)}>
                    Open
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Dialog open={!!selected} onClose={closeReview} fullWidth maxWidth="lg">
        <DialogTitle>Check this week</DialogTitle>
        <DialogContent>
          {detailQuery.isLoading && <CircularProgress />}
          {detailQuery.data && (
            <Box>
              <Typography gutterBottom>
                {detailQuery.data.ownerName} · {detailQuery.data.weekStart} → {detailQuery.data.weekEnd} ·{' '}
                {weekStatusLabel(detailQuery.data.status)}
              </Typography>
              <Typography variant="body2" gutterBottom>
                Expected {detailQuery.data.expectedHours} hours · Worked {detailQuery.data.workedHours} · Leave{' '}
                {detailQuery.data.leaveHours} · Difference {detailQuery.data.varianceHours}
              </Typography>
              {(detailQuery.data.warnings || []).map((w) => (
                <Alert key={w.code + w.message} severity="info" sx={{ mb: 1 }}>
                  {w.message}
                </Alert>
              ))}
              <TimesheetWeekGrid
                week={detailQuery.data}
                editable={false}
                emptyMessage="Nothing is filled in this week."
                hint="Click the hours to see what was entered."
              />
              <TextField
                fullWidth
                multiline
                minRows={2}
                label="Note for them (optional)"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                sx={{ mt: 2 }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeReview}>Close</Button>
          {detailQuery.data?.canReview && detailQuery.data?.status === 'approved' && (
            <Button onClick={() => reviewMut.mutate({ action: 'reopened' })}>Reopen</Button>
          )}
          {detailQuery.data?.canReview && detailQuery.data?.status === 'submitted' && (
            <>
              <Button color="error" onClick={() => reviewMut.mutate({ action: 'rejected' })}>
                Send back
              </Button>
              <Button variant="contained" onClick={() => reviewMut.mutate({ action: 'approved' })}>
                Approve
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
